export const stockQueryKeys = {
  all: ['stock'] as const,
  // Balances
  balances: {
    all: ['stock', 'balances'] as const,
    list: (filters: object) => ['stock', 'balances', 'list', filters] as const,
    detail: (productId: string, filters?: object) =>
      ['stock', 'balances', 'detail', productId, filters] as const,
    matrix: (filters: object) => ['stock', 'balances', 'matrix', filters] as const,
    matrixView: (filters: object) => ['stock', 'balances', 'matrix-view', filters] as const,
    atDate: (filters: object) => ['stock', 'balances', 'at-date', filters] as const,
    locationHistory: (productId: string, locationId: string, filters?: object) =>
      ['stock', 'balances', 'location-history', productId, locationId, filters] as const,
    aggregateHistory: (productId: string, filters?: object) =>
      ['stock', 'balances', 'aggregate-history', productId, filters] as const,
  },
  productCosts: {
    all: ['stock', 'product-costs'] as const,
    latest: (productIds: string[]) => ['stock', 'product-costs', 'latest', productIds] as const,
  },
  productTotals: {
    all: ['stock', 'product-totals'] as const,
    latest: (productIds: string[]) => ['stock', 'product-totals', 'latest', productIds] as const,
  },
  alerts: {
    all: ['stock', 'alerts'] as const,
    summary: (filters: object) => ['stock', 'alerts', 'summary', filters] as const,
  },
  locationTracking: {
    list: () => ['stock', 'location-tracking', 'list'] as const,
    detail: (locationId: string) => ['stock', 'location-tracking', locationId] as const,
  },
  operations: {
    all: ['stock', 'operations'] as const,
    list: (filters: object) => ['stock', 'operations', 'list', filters] as const,
    detail: (operationId: string) => ['stock', 'operations', 'detail', operationId] as const,
    viewCounts: (filters: object) => ['stock', 'operations', 'view-counts', filters] as const,
  },
  // Movements
  movements: {
    all: ['stock', 'movements'] as const,
    list: (filters: object) => ['stock', 'movements', 'list', filters] as const,
    salesStats: (filters: object) => ['stock', 'movements', 'sales-stats', filters] as const,
    productSalesDashboard: (productId: string, days: number) =>
      ['stock', 'movements', 'product-sales-dashboard', productId, days] as const,
    productProductionDashboard: (productId: string, days: number) =>
      ['stock', 'movements', 'product-production-dashboard', productId, days] as const,
    dailySummary: (filters: object) => ['stock', 'movements', 'daily-summary', filters] as const,
    detail: (id: string) => ['stock', 'movements', 'detail', id] as const,
  },
  // Receipts
  receipts: {
    all: ['stock', 'receipts'] as const,
    list: (filters: object) => ['stock', 'receipts', 'list', filters] as const,
    detail: (id: string) => ['stock', 'receipts', 'detail', id] as const,
    lastPurchasePrices: (payload: object) =>
      ['stock', 'receipts', 'last-purchase-prices', payload] as const,
    matchProducts: (payload: object) => ['stock', 'receipts', 'match-products', payload] as const,
  },
  supplierProductMappings: {
    all: ['stock', 'supplier-product-mappings'] as const,
    list: (filters: object) => ['stock', 'supplier-product-mappings', 'list', filters] as const,
  },
  // Transfers
  transfers: {
    all: ['stock', 'transfers'] as const,
    list: (filters: object) => ['stock', 'transfers', 'list', filters] as const,
    detail: (id: string) => ['stock', 'transfers', 'detail', id] as const,
  },
  sellerWorkspace: {
    all: ['stock', 'seller-workspace'] as const,
    context: () => ['stock', 'seller-workspace', 'context'] as const,
    materials: (filters: object) => ['stock', 'seller-workspace', 'materials', filters] as const,
    products: (filters: object) => ['stock', 'seller-workspace', 'products', filters] as const,
    balances: (filters: object) => ['stock', 'seller-workspace', 'balances', filters] as const,
    balanceCategories: (locationId: string) =>
      ['stock', 'seller-workspace', 'balance-categories', locationId] as const,
    transferRequests: (filters: object) =>
      ['stock', 'seller-workspace', 'transfer-requests', filters] as const,
    transferRequest: (id: string) => ['stock', 'seller-workspace', 'transfer-request', id] as const,
    writeOffRequests: (filters: object) =>
      ['stock', 'seller-workspace', 'write-off-requests', filters] as const,
    writeOffRequest: (id: string) =>
      ['stock', 'seller-workspace', 'write-off-request', id] as const,
    productionRequests: (filters: object) =>
      ['stock', 'seller-workspace', 'production-requests', filters] as const,
    productionPlan: (locationId: string) =>
      ['stock', 'seller-workspace', 'production-plan', locationId] as const,
    productionRequest: (id: string) =>
      ['stock', 'seller-workspace', 'production-request', id] as const,
    productsWithRecipes: () => ['stock', 'seller-workspace', 'products-with-recipes'] as const,
    flattenActiveRecipePerUnit: (productId: string) =>
      ['stock', 'seller-workspace', 'recipes', 'flatten', productId] as const,
    flattenSelectedRecipes: (productIds: readonly string[]) =>
      ['stock', 'seller-workspace', 'recipes', 'flatten-selected', productIds] as const,
    receiptDrafts: (filters: object) =>
      ['stock', 'seller-workspace', 'receipt-drafts', filters] as const,
  },
  // Write-offs
  writeOffs: {
    all: ['stock', 'write-offs'] as const,
    list: (filters: object) => ['stock', 'write-offs', 'list', filters] as const,
    detail: (id: string) => ['stock', 'write-offs', 'detail', id] as const,
  },
  // Supplier returns
  supplierReturns: {
    all: ['stock', 'supplier-returns'] as const,
    list: (filters: object) => ['stock', 'supplier-returns', 'list', filters] as const,
    detail: (id: string) => ['stock', 'supplier-returns', 'detail', id] as const,
  },
  // Productions
  productions: {
    all: ['stock', 'productions'] as const,
    list: (filters: object) => ['stock', 'productions', 'list', filters] as const,
    detail: (id: string) => ['stock', 'productions', 'detail', id] as const,
  },
  // Recipes
  recipes: {
    all: ['stock', 'recipes'] as const,
    list: (filters: object) => ['stock', 'recipes', 'list', filters] as const,
    categoryGroups: () => ['stock', 'recipes', 'category-groups'] as const,
    detail: (id: string) => ['stock', 'recipes', 'detail', id] as const,
    preview: (payload: object) => ['stock', 'recipes', 'preview', payload] as const,
    flattenActiveRecipePerUnit: (productId: string) =>
      ['stock', 'recipes', 'flatten', productId] as const,
    productsWithRecipes: () => ['stock', 'recipes', 'products-with-recipes'] as const,
    usedIn: (recipeId: string) => ['stock', 'recipes', 'used-in', recipeId] as const,
    activeForProduct: (productId: string) =>
      ['stock', 'recipes', 'active-for-product', productId] as const,
  },
  laborRates: {
    all: ['stock', 'labor-rates'] as const,
  },
  overheadRates: {
    all: ['stock', 'overhead-rates'] as const,
  },
  ingredients: {
    all: ['stock', 'ingredients'] as const,
    list: (filters: object) => ['stock', 'ingredients', 'list', filters] as const,
    detail: (id: string) => ['stock', 'ingredients', 'detail', id] as const,
    suppliers: (id: string) => ['stock', 'ingredients', 'suppliers', id] as const,
    purchaseSummary: (id: string, days?: number, asOfDate?: string) =>
      ['stock', 'ingredients', 'purchase-summary', id, days ?? null, asOfDate ?? null] as const,
    purchaseHistory: (id: string, filters?: object) =>
      ['stock', 'ingredients', 'purchase-history', id, filters] as const,
    priceStats: (id: string, days?: number) =>
      ['stock', 'ingredients', 'price-stats', id, days] as const,
    usedInRecipes: (id: string) => ['stock', 'ingredients', 'used-in-recipes', id] as const,
  },
  supplies: {
    all: ['stock', 'supplies'] as const,
    list: (filters: object) => ['stock', 'supplies', 'list', filters] as const,
  },
  materials: {
    all: ['stock', 'materials'] as const,
    list: (filters: object) => ['stock', 'materials', 'list', filters] as const,
  },
  // Inventories
  inventories: {
    all: ['stock', 'inventories'] as const,
    list: (filters: object) => ['stock', 'inventories', 'list', filters] as const,
    detail: (id: string) => ['stock', 'inventories', 'detail', id] as const,
    productsMeta: (id: string) => ['stock', 'inventories', id, 'products-meta'] as const,
    valuation: (id: string) => ['stock', 'inventories', id, 'valuation'] as const,
    overviewCards: (filters: object) =>
      ['stock', 'inventories', 'overview-cards', filters] as const,
    ocr: (id: string) => ['stock', 'inventories', id, 'ocr'] as const,
  },
  // B2B
  b2b: {
    customers: {
      all: ['stock', 'b2b', 'customers'] as const,
      list: (filters: object) => ['stock', 'b2b', 'customers', 'list', filters] as const,
      detail: (id: string) => ['stock', 'b2b', 'customers', 'detail', id] as const,
      stats: (id: string) => ['stock', 'b2b', 'customers', 'stats', id] as const,
    },
    prices: {
      all: ['stock', 'b2b', 'prices'] as const,
      list: (customerId: string, filters: object) =>
        ['stock', 'b2b', 'prices', 'list', customerId, filters] as const,
      effective: (customerId: string, onDate: string) =>
        ['stock', 'b2b', 'prices', 'effective', customerId, onDate] as const,
      standard: ['stock', 'b2b', 'prices', 'standard'] as const,
    },
    sales: {
      all: ['stock', 'b2b', 'sales'] as const,
      list: (filters: object) => ['stock', 'b2b', 'sales', 'list', filters] as const,
      detail: (id: string) => ['stock', 'b2b', 'sales', 'detail', id] as const,
      shipments: {
        list: (saleId: string) => ['stock', 'b2b', 'sales', 'shipments', 'list', saleId] as const,
        detail: (saleId: string, shipmentId: string) =>
          ['stock', 'b2b', 'sales', 'shipments', 'detail', saleId, shipmentId] as const,
      },
    },
  },
  salesInvoices: {
    all: ['stock', 'sales-invoices'] as const,
    list: (filters: object) => ['stock', 'sales-invoices', 'list', filters] as const,
    detail: (id: string) => ['stock', 'sales-invoices', 'detail', id] as const,
    xmlPreview: (id: string, updatedAt: string | null) =>
      ['stock', 'sales-invoices', 'xml-preview', id, updatedAt ?? 'base'] as const,
  },
  platformSales: {
    all: ['stock', 'platform-sales'] as const,
    list: (filters: object) => ['stock', 'platform-sales', 'list', filters] as const,
    detail: (id: string) => ['stock', 'platform-sales', 'detail', id] as const,
  },
  hotReports: {
    all: ['stock', 'hot-reports'] as const,
    list: (filters: object) => ['stock', 'hot-reports', 'list', filters] as const,
    detail: (id: string) => ['stock', 'hot-reports', 'detail', id] as const,
    products: ['stock', 'hot-reports', 'products'] as const,
  },
};
